'use client'

import { useRef, useState } from 'react'
import { MdDelete, MdUploadFile } from 'react-icons/md'
import { toast } from 'react-toastify'
import { useImageCrop } from '@/components/image-crop'
import {
    Letterhead,
    LetterheadImageSlot,
    removeLetterheadImage,
    uploadLetterheadImage,
} from '@/lib/api/letterheads/letterhead-api'

interface Props {
    letterheadId: number
    slot: LetterheadImageSlot
    title: string
    hint: string
    url?: string | null
    width?: number | null
    height?: number | null
    disabled?: boolean
    onChange: (letterhead: Letterhead) => void
}

/** 2 MB — o mesmo teto que o backend aplica; recusar aqui poupa o upload. */
const MAX_IMAGE_BYTES = 2 * 1024 * 1024

/**
 * Envio de uma das imagens do timbrado.
 *
 * As dimensões são exibidas porque elas não são decorativas: o sistema calcula
 * a altura que a faixa vai ocupar na página a partir da proporção do arquivo, e
 * ver "1654 x 300" ao lado da prévia explica por que o espaço reservado ficou do
 * tamanho que ficou.
 */
export default function LetterheadImageField({
    letterheadId,
    slot,
    title,
    hint,
    url,
    width,
    height,
    disabled,
    onChange,
}: Props) {
    const inputRef = useRef<HTMLInputElement>(null)
    const [busy, setBusy] = useState(false)
    // A arte embarca no PDF: saída em PNG (o @react-pdf/renderer não lê WebP e a
    // transparência do cabeçalho/rodapé precisa sobreviver). 1654 px = A4 em 150 dpi.
    const { cropFile, cropDialog } = useImageCrop({
        title: 'Ajustar imagem do timbrado',
        output: { maxWidth: 1654, mimeType: 'image/png' },
    })

    const handleSelect = async (rawFile?: File) => {
        if (!rawFile) return
        const file = await cropFile(rawFile)
        if (!file) {
            if (inputRef.current) inputRef.current.value = ''
            return
        }
        // O teto vale para o arquivo CORTADO, que é o que embarca no PDF.
        if (file.size > MAX_IMAGE_BYTES) {
            toast.error(
                'A imagem passa de 2 MB. Exporte em 150 dpi — a arte é embarcada no PDF e um arquivo grande deixa o documento pesado demais para enviar.',
            )
            return
        }
        setBusy(true)
        try {
            const response = await uploadLetterheadImage(letterheadId, slot, file)
            if (response.success && response.result) {
                onChange(response.result)
                toast.success('Imagem enviada')
            } else {
                toast.error(response.error || 'Erro ao enviar a imagem')
            }
        } finally {
            setBusy(false)
            if (inputRef.current) inputRef.current.value = ''
        }
    }

    const handleRemove = async () => {
        setBusy(true)
        try {
            const response = await removeLetterheadImage(letterheadId, slot)
            if (response.success && response.result) {
                onChange(response.result)
                toast.success('Imagem removida')
            } else {
                toast.error(response.error || 'Erro ao remover a imagem')
            }
        } finally {
            setBusy(false)
        }
    }

    return (
        <div className="form-control">
            {/*
                Flex explícito em vez da classe `label` do DaisyUI: o CSS global do
                projeto redefine `.label`, e as dimensões saíam coladas no título.
            */}
            <div className="flex items-center justify-between gap-2 mb-1 px-1">
                <span className="label-text font-medium">{title}</span>
                {width && height ? (
                    <span className="text-xs text-base-content/50">
                        {width} x {height} px
                    </span>
                ) : null}
            </div>

            <div className="border border-dashed border-base-300 rounded-xl p-4 flex flex-col gap-3 bg-base-200/30">
                {url ? (
                    <div className="bg-white rounded-lg border border-base-300/50 p-2 flex items-center justify-center">
                        <img
                            src={url}
                            alt={title}
                            className="max-h-40 w-auto object-contain"
                        />
                    </div>
                ) : (
                    <div className="flex items-center justify-center h-24 text-base-content/40 text-sm">
                        Nenhuma imagem enviada
                    </div>
                )}

                <p className="text-xs text-base-content/60 leading-relaxed">
                    {hint}
                </p>

                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        className="btn btn-sm btn-outline gap-2"
                        disabled={disabled || busy}
                        onClick={() => inputRef.current?.click()}
                    >
                        <MdUploadFile className="text-lg" />
                        {url ? 'Substituir' : 'Enviar imagem'}
                    </button>
                    {url && (
                        <button
                            type="button"
                            className="btn btn-sm btn-ghost text-error gap-2"
                            disabled={disabled || busy}
                            onClick={handleRemove}
                        >
                            <MdDelete className="text-lg" />
                            Remover
                        </button>
                    )}
                    {busy && (
                        <span className="loading loading-spinner loading-sm text-secondary" />
                    )}
                </div>

                <input
                    ref={inputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="hidden"
                    onChange={e => handleSelect(e.target.files?.[0])}
                />
                {cropDialog}
            </div>
        </div>
    )
}
